import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { messagesCa, messagesEs } from '../../Helpers/calendar-messages-es';
import { CalendarEvent } from './CalendarEvent';
import { CalendarModal } from './CalendarModal';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { recuperarDetallMesComplet, IniciMesConsultat, FinalMesConsultat } from '../../actions/configCalendariAction';
import { uiMostraCalendarModal, uiMostraDiaConsultatAction } from '../../actions/ui';
import { convertirMinutsHores, convertirMinutsMinuts } from '../../Helpers/funcionsDates';
import { MenuPrincipal } from '../ui/MenuPrincipal';
import { UsuariLoginat } from '../ui/UsuariLoginat';
import SpinerLoader from '../../Helpers/SpinerLoader';

export const CalendarScreen = () => {
    const localizer = momentLocalizer(moment);
    const dispatch = useDispatch();
    const [t, i18n] = useTranslation('generic');
    moment.locale(t('Generics.idiomaDatetime'));

    const { calendari, auth, ui } = useSelector(
        state => ({ calendari: state.calendari, auth: state.auth, ui: state.ui }),
        shallowEqual
    );

    const [selectedDate, setSelectedDate] = useState(new Date());
    // Guardem si hi havia un msgOk mentre el modal estava obert
    const msgOkDurantModalRef = useRef(false);

    // ── Al muntar: forçar mes actual (evita quedar penjat en un mes anterior) ──
    useEffect(() => {
        setSelectedDate(new Date());
    }, []);

    // ── Loader basat en el flag del reducer, no en events.length ──────────────
    const loading = !!calendari.loadingDetallMes;

    // ── Detectar msgOk mentre el modal és obert ───────────────────────────────
    useEffect(() => {
        if (ui.modalCalendar && ui.msgOk) {
            msgOkDurantModalRef.current = true;
        }
    }, [ui.msgOk, ui.modalCalendar]);

    // ── Re-fetch quan es tanca el modal si hi havia canvis ────────────────────
    useEffect(() => {
        if (!ui.modalCalendar && msgOkDurantModalRef.current && calendari.inici && calendari.final) {
            msgOkDurantModalRef.current = false;
            dispatch(recuperarDetallMesComplet(auth.tokenJwt, calendari.inici, calendari.final));
        }
    }, [ui.modalCalendar]);

    // ── onNavigate obligatori quan es passa `date` com a prop controlada ──────
    const onNavigate = useCallback((newDate) => {
        setSelectedDate(newDate);
    }, []);

    // ── Canvi de mes: 1 sola crida ────────────────────────────────────────────
    const onRangeChange = useCallback((range) => {
        const start = moment(range.start);
        const end   = moment(range.end);

        if (start.date() !== 1) start.add(1, 'month');
        start.startOf('month');

        const inici = start.format('YYYY-MM-DD');
        const final = end.format('YYYY-MM-DD');

        setSelectedDate(start.toDate());
        dispatch(IniciMesConsultat(inici));
        dispatch(FinalMesConsultat(final));
        dispatch(recuperarDetallMesComplet(auth.tokenJwt, inici, final));
    }, [dispatch, auth.tokenJwt]);

    const onSelectSlot = useCallback((slotInfo) => {
        const dia = moment(slotInfo.start).format('YYYY-MM-DD');
        dispatch(uiMostraDiaConsultatAction({ dia }));
        dispatch(uiMostraCalendarModal());
    }, [dispatch]);

    const onSelectEvent = useCallback((event) => {
        const dia = moment(event.DataInici_IncidenciaFix).format('YYYY-MM-DD');
        dispatch(uiMostraDiaConsultatAction({ dia }));
        dispatch(uiMostraCalendarModal());
    }, [dispatch]);

    // ── Events des de detallMes ───────────────────────────────────────────────
    const events = useMemo(() => {
        const fixatgesMes    = calendari.detallMes?.fixatgesMes    ?? [];
        const incidenciesMes = calendari.detallMes?.incidenciesMes ?? [];
        return [
            ...incidenciesMes.map(d => ({
                DataInici_IncidenciaFix:        moment(d.DataInici_IncidenciaFix).toDate(),
                DataFi_IncidenciaFix:           moment(d.DataFi_IncidenciaFix).toDate(),
                Observacions_IncidenciaFix:      d.Observacions_IncidenciaFix,
                IdTipusIncidencia_IncidenciaFix: d.IdTipusIncidencia_IncidenciaFix,
                isMine: true,
                dissabtes: false
            })),
            ...fixatgesMes.map(d => ({
                DataInici_IncidenciaFix: moment(d.dia).toDate(),
                DataFi_IncidenciaFix:   moment(d.dia).toDate(),
                existeixAlerta: d.existeixAlerta,
                esLaborable:    d.esLaborable,
                idTipusAlerta:  d.idTipusAlerta,
                isMine:   false,
                dissabte: Array.isArray(d.fixatges) && d.fixatges.length > 0
            }))
        ];
    }, [calendari.detallMes]);

    // ── Info resum lateral ────────────────────────────────────────────────────
    const info = useMemo(() => calendari.detallMes?.infoMes ?? {}, [calendari.detallMes]);

    const computHores      = convertirMinutsHores(info.computMinutsMes        || 0);
    const computMinuts     = convertirMinutsMinuts(info.computMinutsMes       || 0);
    const horesTreballades = convertirMinutsHores(info.computMinutsFixatge    || 0);
    const minutsTreballats = convertirMinutsMinuts(info.computMinutsFixatge   || 0);
    const horesIncidencia  = convertirMinutsHores(info.computMinutsIncidencia  || 0);
    const minutsIncidencia = convertirMinutsMinuts(info.computMinutsIncidencia || 0);
    const horesBossaHores  = convertirMinutsHores(info.computMiniutsBossaHores  || 0);
    const minutsBossaHores = convertirMinutsMinuts(info.computMiniutsBossaHores || 0);
    const horesTotals = convertirMinutsHores(
        (info.computMinutsFixatge || 0) + (info.computMinutsIncidencia || 0) + (info.computMiniutsBossaHores || 0)
    );
    const minutsTotals = convertirMinutsMinuts(
        (info.computMinutsFixatge || 0) + (info.computMinutsIncidencia || 0) + (info.computMiniutsBossaHores || 0)
    );
    const mes = i18n.language === 'ca' ? info.mes_ca : info.mes_es;

    const eventStyleGetter = useCallback((event) => {
        let style = { backgroundColor: '#ff9103', borderRadius: '0px', opacity: 1, display: 'block', color: '#2d374b' };
        if (!event.isMine && event.existeixAlerta && (event.idTipusAlerta == '1' || event.idTipusAlerta == '2')) {
            style.backgroundColor = '#CCCCCC';
        } else if (!event.isMine && event.existeixAlerta && event.esLaborable) {
            style.backgroundColor = '#e44554'; style.color = '#FFFFFF';
        } else if (!event.isMine && !event.existeixAlerta && event.esLaborable) {
            style.backgroundColor = '#00c884';
        } else if (!event.isMine && !event.existeixAlerta && !event.esLaborable) {
            style.backgroundColor = '#FFFFFF';
        }
        if (event.dissabte) style.backgroundColor = '#00c884';
        return { style };
    }, []);

    return (
        <div className='position-relative'>
            <div className='card card-shadow card-login'>
                <div className='row'>
                    <div className='col-md-8'>
                        <div className="card-body">
                            <div className='visible-xs'>
                                <UsuariLoginat />
                                <hr />
                            </div>
                            <MenuPrincipal />
                            <h3>{t('Header.Historic')}</h3>

                            {/* Contenidor amb altura fixa - react-big-calendar ho necessita */}
                            <div style={{ position: 'relative', height: '600px' }}>

                                {/* Overlay loader mentre carrega el mes */}
                                {loading && (
                                    <div style={{
                                        position: 'absolute', inset: 0, zIndex: 10,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: 'rgba(255,255,255,0.75)',
                                        borderRadius: '8px'
                                    }}>
                                        <SpinerLoader />
                                        <p className="mt-3 text-muted">{t('Generics.carregant') || 'Carregant el mes...'}</p>
                                    </div>
                                )}

                                {/* Calendari sempre muntat - no desapareix al canviar de mes */}
                                <div style={{
                                    height: '100%',
                                    opacity: loading ? 0.3 : 1,
                                    pointerEvents: loading ? 'none' : 'auto',
                                    transition: 'opacity 0.2s'
                                }}>
                                    <Calendar
                                        localizer={localizer}
                                        events={events}
                                        startAccessor="DataInici_IncidenciaFix"
                                        endAccessor="DataFi_IncidenciaFix"
                                        onSelectEvent={onSelectEvent}
                                        messages={i18n.language === 'ca' ? messagesCa : messagesEs}
                                        eventPropGetter={eventStyleGetter}
                                        onRangeChange={onRangeChange}
                                        selectable={!loading}
                                        onView={() => {}}
                                        view="month"
                                        views={['month']}
                                        onSelectSlot={onSelectSlot}
                                        date={selectedDate}
                                        onNavigate={onNavigate}
                                        components={{ event: CalendarEvent }}
                                    />
                                </div>
                            </div>

                            <CalendarModal />
                        </div>
                    </div>

                    <div className='col-md-4 d-md-flex flex-column bg-soft-primary p-8 p-md-5 bg-comb'>
                        <div className='hidden-xs'>
                            <UsuariLoginat />
                            <hr />
                        </div>

                        {loading ? (
                            <>
                                <SpinerLoader />
                                <p className="text-muted text-center mt-2">{t('Generics.carregant') || 'Carregant dades del mes...'}</p>
                            </>
                        ) : Object.keys(info).length > 0 ? (
                            <>
                                <h5 className='mt-3'>{mes} {computHores} {t('Generics.hores')} {t('Generics.caracIoY')} {computMinuts} {t('Generics.minuts')}</h5>
                                <p>{t('ResumHores.horesTreball')} {horesTreballades} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsTreballats} {t('Generics.minuts')}</p>
                                {(horesIncidencia > 0 || minutsIncidencia > 0) &&
                                    <p>{t('ResumHores.justificades')} {horesIncidencia} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsIncidencia} {t('Generics.minuts')}</p>
                                }
                                {(horesBossaHores > 0 || minutsBossaHores > 0) &&
                                    <p>{t('ResumHores.bossaHores')} {horesBossaHores} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsBossaHores} {t('Generics.minuts')}</p>
                                }
                                <p>{t('ResumHores.diaTotal')} {horesTotals} {t('Generics.hores')} {t('Generics.caracIoY')} {minutsTotals} {t('Generics.minuts')}</p>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
            <figure className="position-absolute top-0 end-0 zi-n1 d-none d-sm-block mt-n7 me-n10 width4rem">
                <img className="img-fluid" src="../assets/svg/components/pointer-up.svg" alt="Image Description" />
            </figure>
            <figure className="position-absolute bottom-0 start-0 d-none d-sm-block ms-n10 mb-n10 width15rem">
                <img className="img-fluid" src="../assets/svg/components/curved-shape.svg" alt="Image Description" />
            </figure>
        </div>
    );
};